import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { hasActiveCloudResourceUsageDetection } from './scan/connection-scan-state'

/** Both connection setup and Services observe the connection's queued discovery. */
export function useCloudResourceUsageDetection(
  connectionId?: string | null,
  enabled = true
) {
  const queryClient = useQueryClient()
  const connectionsQuery = useQuery({
    ...trpc.integrations.getCloudConnections.queryOptions(),
    enabled,
    refetchInterval: (query) =>
      hasActiveCloudResourceUsageDetection(query.state.data) ? 1000 : false,
  })
  const mutation = useMutation({
    ...trpc.integrations.detectCloudResourceUsage.mutationOptions(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.getCloudConnections.queryKey(),
      })
    },
  })
  const account = connectionsQuery.data
    ?.flatMap((connection) => connection.accounts)
    .find((item) => item.connectionId === connectionId)
  const state = account?.resourceUsageDetectionState ?? null
  const running =
    mutation.isPending ||
    state === 'waiting' ||
    state === 'active' ||
    state === 'delayed'
  const previous = useRef({ connectionId, running })
  useEffect(() => {
    const finished =
      previous.current.connectionId === connectionId &&
      previous.current.running &&
      !running
    previous.current = { connectionId, running }
    if (!finished) return
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.listProviderConnections.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.checker.listProviderChecks.queryKey(),
    })
  }, [connectionId, running, queryClient])
  return { connectionsQuery, account, state, running, mutation }
}
